/**
 * A board's special rules (MapDef.specialRules, plus chips for what its
 * fixedSetup decides). One card shared by the game setup page and the map
 * details page, so the two never describe a board differently.
 */
import React, { useState } from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import type { MapDef } from '@warcarp/api-types'
import { describeFixedSetup } from '../../lib/fixedSetup'
import { describeSetupValue } from '../../lib/gameSetupDraft'
import { SetupCard } from '../game/setup/SetupCards'
import { SETUP_AMBER } from '../game/setup/SetupControls'

/** What the board's special rules fix, as short chips ("Players: 2–6"). */
export function specialRuleChips(map: MapDef): string[] {
  const fixed = describeFixedSetup(map)
  if (!fixed) return []
  return [
    `Players: ${fixed.players}`,
    `Territory selection: ${fixed.homeArea.toLowerCase()}`,
    'Starting troops: set by map',
    ...Object.entries(map.fixedSetup?.config ?? {}).map(([key, value]) => {
      const shown = describeSetupValue(key, value) ?? fixed.pinnedConfig.find((p) => p.key === key)
      return shown ? `${shown.label}: ${shown.value}` : key
    }),
  ]
}

/**
 * The board's special rules, collapsed by default: the first rule (clipped)
 * and the chips of what the map fixes; "Show all rules" expands the list.
 */
export function SpecialRulesCard({ map, padding, testID = 'setup-special-rules' }: {
  map: MapDef
  padding: number
  /** The toggle is `${testID}-toggle`. */
  testID?: string
}) {
  const chips = specialRuleChips(map)
  const rules = map.specialRules ?? []
  const [open, setOpen] = useState(false)
  const shown = open ? rules : rules.slice(0, 1)
  return <SetupCard padding={padding} accent={SETUP_AMBER} testID={testID}>
    <View className="flex-row items-center" style={{ marginBottom: 8, gap: 10 }}>
      <Text accessibilityRole="header" className="font-stencil text-ink uppercase flex-1" style={{ fontSize: 15 }}>★ Special rules</Text>
      {rules.length > 1 && <TouchableOpacity testID={`${testID}-toggle`} accessibilityRole="button"
        accessibilityLabel={open ? 'Show fewer special rules' : `Show all ${rules.length} special rules`}
        accessibilityState={{ expanded: open }} onPress={() => setOpen((v) => !v)} hitSlop={8}>
        <Text className="font-stencil text-ink text-xs">{open ? 'Show less ▴' : `Show all rules (${rules.length}) ▾`}</Text>
      </TouchableOpacity>}
    </View>
    <View style={{ gap: 4 }}>
      {shown.map((rule) => <View key={rule} className="flex-row items-start">
        <Text className="font-typed text-ink-soft text-sm mr-2">•</Text>
        <Text className="font-typed text-ink text-sm flex-1 leading-5" numberOfLines={open ? undefined : 2}>{rule}</Text>
      </View>)}
    </View>
    {chips.length > 0 && <View className="flex-row flex-wrap" style={{ gap: 8, marginTop: 10 }}>
      {chips.map((chip) => <View key={chip} className="rounded-full border border-olive bg-paper-up" style={{ paddingHorizontal: 10, paddingVertical: 4 }}>
        <Text className="font-typed text-ink text-xs">{chip}</Text>
      </View>)}
    </View>}
  </SetupCard>
}
